import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MatDrawer,
  MatSidenav,
  MatSidenavModule,
} from '@angular/material/sidenav';
import { Router, RouterOutlet } from '@angular/router';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { TranslateModule } from '@ngx-translate/core';
import { MainHeaderComponent } from '../main-header/main-header.component';
import { LoaderComponent } from '../common/loader/loader.component';
import {
  EMAIL_QUERY_PARAM,
  STORAGE_NAMES,
} from '@app-shared/constants/constants';
import { Subscription } from 'rxjs';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { delay } from 'rxjs/operators';
import { NavSidenavComponent } from '../nav-sidenav/nav-sidenav.component';
import { EmailsSideNavComponent } from '../emails-sidenav/emails-side-nav.component';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { encodeInfoMailAccount } from '@app-utils/search-params';
import { getFolderByType } from '@app-utils/folders';
import { DrawerComponent } from '../common/drawer/drawer.component';
import { DrawerService } from '@app-services/drawer.service';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { IdleTimeoutService } from '@app-services/idle-timeout.service';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import { RoleType } from '@app-types/enums/role-type';
import { PermissionService } from '@app-services/permission/permission.service';
import { PermissionType } from '@app-types/enums/permission-type';
import packageInfo from '../../../../package.json';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { SnackbarService } from '@app-services/snackbar.service';
import { IntervalService } from '@app-services/interval.service';
import { CacheBusterService } from '@app-services/сache-buster.service';
import { FeClient } from '@app-services/api/clients/fe.client';
import { makeId } from '@app-utils/common';
import { LocalStorageService } from '@app-services/local-storage.service';
import { environment } from '@app-environments/environment';
import { ApiRequest } from '@app-services/api/api.request';
import { LoggingHandler } from '@app-services/errors/logging.service';

const { EMAILS, ERRORS, ORGANIZATIONS, PROVIDERS } = ROUTES;
const { FORBIDDEN, SERVER_ERROR } = ERRORS;
const { CLIENT_ID } = STORAGE_NAMES;

@Component({
  selector: 'app-main',
  imports: [
    RouterOutlet,
    MatSidenavModule,
    MainHeaderComponent,
    TranslateModule,
    LoaderComponent,
    NavSidenavComponent,
    EmailsSideNavComponent,
    DrawerComponent,
    SomethingWentWrongComponent,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('sidenav') public sideNav: MatSidenav;
  @ViewChild('emailsDrawer') matDriver: MatDrawer;
  @ViewChild(DrawerComponent)
  set drawer(drawer: DrawerComponent) {
    this.drawerService.setDrawerComponent(drawer);
  }
  public isLoading = false;
  public isLoadingPage = true;
  public isError = false;
  public isOpenLeftMenu = false;
  public visibleFullScreen = false;

  private useMobileViewSubscription: Subscription;

  clientId: string;

  constructor(
    public matchError: MatchError,
    private router: Router,
    private permissionService: PermissionService,
    private mobileObserverService: MobileObserverService,
    private observer: BreakpointObserver,
    protected userWorkspaceService: UserWorkspaceService,
    private idleTimeoutService: IdleTimeoutService,
    private drawerService: DrawerService,
    private feClient: FeClient,
    private snackbarService: SnackbarService,
    private intervalService: IntervalService,
    private cacheBusterService: CacheBusterService,
    private localStorageService: LocalStorageService,
    private apiRequest: ApiRequest,
    private loggingHandler: LoggingHandler
  ) {}

  @HostListener('mousemove')
  @HostListener('keydown')
  @HostListener('click')
  userActivityDetected(): void {
    this.idleTimeoutService.userActivityDetected();
  }

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.isOpenLeftMenu = isMobile));

    this.logData();
    await this.loadData();
    this.checkVersionInterval();
  }

  logData(): void {
    let clientId = this.localStorageService.getData(CLIENT_ID);
    if (!clientId) {
      clientId = makeId(20);
      this.localStorageService.setData(CLIENT_ID, clientId);
    }
    this.clientId = clientId;
    this.tokenRenew();
    console.log(`CLIENTID=${this.clientId}`);
    console.log(`PM3 ${packageInfo.version}`);
  }

  public tokenRenew(): void {
    this.intervalService.startInterval(
      'tokenRefreshInterval',
      () => {
        this.apiRequest.get(environment.renewUri);
        this.loggingHandler.create('information', 'renew token');
      },
      environment.userRenewTokenIntervalMs
    );
  }

  redirectMail(): void {
    if (this.userWorkspaceService.mailAccounts?.[0]?.mailAccountId) {
      const folder = getFolderByType(
        this.userWorkspaceService.userFolders,
        this.userWorkspaceService.mailAccounts[0].mailAccountId,
        MailFolderType.Inbox
      );
      if (!folder) {
        this.router.navigateByUrl(`/${EMAILS}`);
        !this.isOpenLeftMenu && (this.isOpenLeftMenu = true);
        return;
      }
      const infoMailAcc = encodeInfoMailAccount(
        this.userWorkspaceService.mailAccounts[0].mailAccountId,
        folder,
        MailFolderType.Inbox
      );
      this.router.navigate([EMAILS], {
        queryParams: infoMailAcc
          ? {
              [EMAIL_QUERY_PARAM]: infoMailAcc,
            }
          : {},
      });
      !this.isOpenLeftMenu && (this.isOpenLeftMenu = true);
    } else {
      this.router.navigateByUrl(`/${EMAILS}`);
    }
  }

  public async loadData(): Promise<void> {
    !this.isLoadingPage && (this.isLoading = true);
    this.isError = false;
    try {
      !this.userWorkspaceService.user &&
        (await this.userWorkspaceService.getUserData());
      !this.userWorkspaceService.userOrganisaions.length &&
        (await this.userWorkspaceService.getUserOrganizations());
      !this.userWorkspaceService.userRole &&
        (await this.userWorkspaceService.getUserRole());

      const hasPermissionOver = await this.permissionService.hasPermissionOver(
        null,
        null,
        PermissionType.CanEverything
      );

      if (!this.userWorkspaceService.currentOrganization) {
        if (hasPermissionOver) {
          const arrUrl = [`/${ORGANIZATIONS}`, `/${PROVIDERS}`];
          if (!arrUrl.includes(window.location.pathname)) {
            this.router.navigateByUrl(`/${FORBIDDEN}`);
          }
          return;
        } else {
          this.router.navigateByUrl(`/${FORBIDDEN}`);
        }
      }

      if (!hasPermissionOver) {
        await this.userWorkspaceService.loadFolders();
        await this.userWorkspaceService.getMailAccounts();
      }

      const lastEndpoints = window.location.pathname.split('/').slice(-2);

      if (lastEndpoints[1] === EMAILS) {
        if (!window.location.search) {
          this.redirectMail();
        }
        this.isOpenLeftMenu = this.visibleFullScreen;
      } else {
        this.isOpenLeftMenu = false;
      }

      this.userWorkspaceService.user.roleId === RoleType.Root &&
        (this.isOpenLeftMenu = false);
    } catch (e: any) {
      if (e.Status === 500) {
        await this.router.navigateByUrl(`/${SERVER_ERROR}`);
      }
      this.isError = true;
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
      this.isLoadingPage = false;
    }
  }

  public onEmailsClick(): void {
    this.redirectMail();
    this.sideNav?.toggle();
    this.matDriver?.open();
  }

  rightMenuOpen(): void {
    !this.visibleFullScreen && this.matDriver?.opened && this.leftMenuOpen();
    this.sideNav?.toggle();
  }

  leftMenuOpen(): void {
    !this.visibleFullScreen && this.sideNav?.opened && this.rightMenuOpen();
    this.matDriver?.toggle();
  }

  async isCurrentVersion(): Promise<boolean> {
    try {
      const res = await this.feClient.getVersion();
      return res.version === packageInfo.version;
    } catch (e) {
      return true;
    }
  }

  openSnackbar(): void {
    const config: MatSnackBarConfig = {
      verticalPosition: 'top',
      horizontalPosition: 'end',
      panelClass: ['snack-bar-version'],
    };
    this.snackbarService.openSnackBar(
      'newVersion',
      'newVersionUpdate',
      config,
      () => this.cacheBusterService.forceReload()
    );
  }

  async checkVersion(): Promise<void> {
    const isCurrentVersion = await this.isCurrentVersion();
    if (!isCurrentVersion) {
      this.openSnackbar();
    }
  }

  checkVersionInterval(): void {
    this.checkVersion();
    this.intervalService.startInterval(
      'checkVersion',
      () => {
        this.checkVersion();
      },
      1000 * 60 * 5
    );
  }

  ngAfterViewInit(): void {
    this.observer
      .observe(['(max-width: 840px)'])
      .pipe(delay(1))
      .subscribe(res => {
        this.visibleFullScreen = !res.matches;
        res.matches && this.matDriver?.opened && this.matDriver?.toggle();
      });
  }

  async ngOnDestroy(): Promise<any> {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
